import { ArrowLeftOutlined } from '@ant-design/icons';
import { declaredBranches } from '@bmo/form-core';
import { Alert, Button, Spin, Typography } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import {
  type ApplicationMetadata,
  applicationsApi,
  type ESignSubmission,
  isResumeUnavailable,
} from '@/api/applications';
import { type WorkflowTemplate, workflowsApi } from '@/api/workflows';
import { AppIdBadge, ClientBadge } from '@/components/ClientBadge';
import { useSetSmartFormPlatform } from '@/components/layout/SmartFormContext';
import { getRuntimeConfig, smartFormLandingUrl } from '@/config/runtimeConfig';
import { isLocked } from '@/form-renderer/bmoStatus';
import {
  type ApplicationSnapshot,
  type SnapshotFieldEntry,
  restoreValuesFromSnapshot,
} from '@/form-renderer/buildApplicationSnapshot';
import { type FieldValue } from '@/form-renderer/FieldRenderer';
import { FormShell } from '@/form-renderer/FormShell';
import { followOptionRedirect } from '@/form-renderer/optionRedirect';
import { pruneStaleChoices } from '@/form-renderer/pruneStaleChoices';
import { useFormBranch } from '@/form-renderer/useFormBranch';

const { Text } = Typography;

/**
 * Advisor-mode Smart Form runtime. Loads the template + mounts FormShell
 * with `mode='advisor'` so the shell honors the real advisor experience
 * (Next-gates on required content, no rail click-through, live values).
 *
 * TWO KEY DESIGN CHOICES for INFLOWS-3 AC05/AC06 (multi-tab isolation):
 *
 *   1. State lives entirely inside the FormShell component tree — no
 *      shared globals, no cross-tab localStorage — so distinct tabs
 *      with distinct URLs never collide. React remount-per-URL is the
 *      isolation primitive.
 *   2. The `applicationId` is threaded via URL param and passed into
 *      FormShell as a `key`, so navigating between two applications in
 *      the same tab forces a full remount (values + uploads + nav state
 *      all reset). Without the key, react-router would keep the same
 *      component tree alive across URL changes and blend state.
 *
 * <h3>The application id arrives LATE</h3>
 * This page also serves `/applications/new`, where there is no id yet and nothing has been sent to
 * BMO at all. BMO mints the id on the advisor's FIRST save — `POST /api/v1/applications`, carrying
 * that save's answers — and every save after it goes to `/applications/:id/save`. The old flow
 * minted the id on page load with an empty "shell" save, which meant an advisor who clicked the
 * wrong workflow and backed straight out still left a permanent record in BMO's database.
 *
 * Two consequences worth knowing before editing this file:
 *   - When the id lands, the URL is rewritten with `history.replaceState`, NOT with a router
 *     navigation. A navigation would re-run the route match and remount the shell, discarding
 *     every answer the advisor just typed — which is the exact work the save was protecting.
 *   - For the same reason the FormShell `key` is pinned to the id the page MOUNTED with (see
 *     `shellKeyRef`), not to the live one. The key exists to separate two DIFFERENT applications;
 *     an id appearing on the one already open is not that case.
 *
 * Saves go to BMO through `applicationsApi.save` — the full application snapshot on every Save and
 * every page advance (ADR-0010 / A12). Nothing is stored on our side, so the resume read below is
 * the only way an advisor's answers come back.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): SWAP POINT — the template load is
 * currently `workflowsApi.get(templateId)`, same call the originator /
 * approver preview paths use. The advisor session (via GAMX / BFF) should
 * carry the workflow-selection context; today we trust the URL because it's
 * minted by our own /advisor/dashboard.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): resume returns 501 until BMO supplies a
 * retrieve-application contract (blockers B5 / B15). The call and its
 * rehydration are wired below, so landing that contract is a server-side
 * change only — see `applicationsApi.get`.
 *
 * TODO(BMO-ADVISOR-INTEGRATION): `mode` ("r" read-only / "rw" read-write,
 * bmo-workflow-platform-spec.md:35) arrives as a query param when navigation
 * came from AdvisorEntryPage's resolver, but FormShell only has
 * `mode: 'preview' | 'advisor'` today — no read-only rendering exists.
 * `mode="advisor"` below is hardcoded regardless of the URL's `mode` value.
 * Real support needs a read-only variant added to FormShell (fields
 * rendered inert, Save/Next/Submit hidden or disabled) before this can be
 * wired up. See docs/pretend-advisor.md Swap-Point Index row #9.
 */
export function AdvisorApplicationPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  // `applicationId` is undefined on the `/new` route — an application BMO has not minted yet.
  const { templateId, applicationId: routeApplicationId } = useParams<{
    templateId: string;
    applicationId: string;
  }>();
  // Advisor's locale drives every downstream string — the workflow header,
  // FormShell's initial language, all field labels. A hardcoded 'en' here
  // would leave a French-locale advisor reading English content until the
  // workflow's Welcome step language picker fires (and workflows with no
  // Welcome step never recover).
  const isFr = i18n.language.startsWith('fr');
  const language: 'en' | 'fr' = isFr ? 'fr' : 'en';
  // The "CLIENT:" badge — reported by the shell, which holds the answers it comes from.
  const [clientName, setClientName] = useState<string | null>(null);
  const [template, setTemplate] = useState<WorkflowTemplate | null>(null);

  // Tell the header which back office this workflow belongs to — it decides
  // which support number the "Contact us" line shows (IFDS for the registered
  // plans, APEX for the subscription agreements). Cleared on unmount so a stale
  // platform cannot outlive this page and show the wrong number elsewhere.
  const setHeaderPlatform = useSetSmartFormPlatform();
  useEffect(() => {
    setHeaderPlatform((template?.platform as 'IFDS' | 'APEX' | undefined) ?? null);
    return () => setHeaderPlatform(null);
  }, [template?.platform, setHeaderPlatform]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // BMO's record for this application, or null until BMO has one — which, on `/new`, is until the
  // advisor's first save lands. `creationTimestamp` in particular is BMO's to own: every save
  // echoes it back unchanged, so it has to survive from the save that minted it (or from a resume
  // read, once BMO's retrieve contract exists) through every later save.
  //
  // Absent again after a reload or on a deep link, which is consistent — a reload loses the
  // advisor's answers too, since nothing is persisted on our side.
  const [metadata, setMetadataState] = useState<ApplicationMetadata | null>(null);
  /**
   * `metadata` as the HANDLERS see it, rather than as the last render closed over it.
   *
   * Since the generation step was folded out of the advisor flow (2026-10-02), the save and the
   * submit happen inside one chain — `FormShell.submitApplication` runs save → deliver → submit
   * with no render between them. So `handleSubmit` cannot read `metadata` out of its closure:
   * `handleSave` has already called `setMetadata` with BMO's reply, but that update has not been
   * committed, and the running chain holds the `onSubmit` captured when the button was drawn
   * either way.
   *
   * Which would be a cosmetic staleness except that `knownBmoStatus` is the value the submit
   * endpoint GATES on (`BmoStatusLadder.IsReadyToSubmit`): a workflow carrying an
   * AdditionalDocuments step requires DOC-UPLOAD-COMPLETE, the chain's own save is what earns it,
   * and a closure read sends the rung BEFORE it — so every such submit came back
   * NOT_READY_TO_SUBMIT.
   *
   * This stays BMO's own last word, which is the whole point of `knownBmoStatus` (the shell's
   * local ladder is deliberately NOT what travels here — see `handleSave`). It is only read from
   * the freshest echo instead of a captured one.
   */
  const metadataRef = useRef<ApplicationMetadata | null>(null);
  /** The only way `metadata` is written, so the ref can never drift from the state. */
  const setMetadata = useCallback(
    (
      next:
        | ApplicationMetadata
        | null
        | ((prev: ApplicationMetadata | null) => ApplicationMetadata | null),
    ) => {
      const resolved = typeof next === 'function' ? next(metadataRef.current) : next;
      metadataRef.current = resolved;
      setMetadataState(resolved);
    },
    [],
  );
  const [initialValues, setInitialValues] = useState<Record<string, FieldValue>>({});
  /** True once a resume has actually read answers back (SFA-8). Drives FormShell's
   *  resume positioning — an application with nothing saved yet should still open
   *  on step one, so this stays false when the read comes back empty. */
  const [isResumed, setIsResumed] = useState(false);
  /**
   * What the RESUME found: the workflow whose content these saved answers were filled against
   * (the branch target when BMO reports a swap, the base form otherwise) and the version label
   * they were filled on. Null on `/new`, and never written again after the resume — it is the
   * sole input to {@link versionNotice}.
   *
   * Pinned here rather than read live off `metadata.templateVersion`, which is NOT the same
   * question. That label tracks the form being filled right now: every save echoes back the
   * version of whichever form the content came from, so an advisor who answers their way onto
   * a subsequent-application form (Carlyle F&A ⇄ its subsequent form) saves under the branch's
   * label, and the base form's label the moment they answer back. Compared against the form on
   * screen, each of those swaps reads as the version having changed underneath them — which is
   * the one thing this banner exists to announce, and the one thing that cannot happen inside a
   * sitting the advisor is driving. Only a resume can find that the form has moved on.
   *
   * The workflow id travels with the label because the two forms carry their own independent
   * version sequences: "v4" on the subsequent form and "v14" on the base one say nothing about
   * each other, so a comparison across them is meaningless even when both are populated.
   */
  const [resumedOn, setResumedOn] = useState<{ formId: string; version: string } | null>(null);
  /**
   * Set when this page opened an EXISTING application but could not read its saved answers back.
   *
   * The overwrite guard. Every save carries the WHOLE application, never a delta (ADR-0010 / A12),
   * so a form reopened blank is not a harmless empty state: the advisor's first Next would send
   * that blank snapshot to BMO over everything they had saved. The runtime therefore refuses to
   * open at all rather than open editable over answers it cannot see.
   *
   *   'unavailable' — no retrieve configured for this environment (the BFF's 501)
   *   'unreadable'  — BMO answered, but without answers we can read: absent from the response, or
   *                   the PGP blob we sent handed back (ADR-0027)
   *
   * `/new` is never guarded — there is nothing saved to overwrite.
   */
  const [resumeBlocked, setResumeBlocked] = useState<'unavailable' | 'unreadable' | null>(null);
  const location = useLocation();

  // The id in play: BMO's answer to our first save once we have it, otherwise whatever the URL
  // carried in. Undefined only on `/new`, before that first save.
  const applicationId = metadata?.applicationId ?? routeApplicationId;

  // ---- Form branching ------------------------------------------------------------------
  // The advisor has answered their way onto a different form. `useFormBranch` owns the fetch,
  // the per-session cache and the composition; FormShell only reports that the answers now
  // select a different form. See `@bmo/form-core/composeBranchedTemplate` for the
  // identity-from-base / content-from-target split the composition applies.
  //
  // Declared ABOVE the mount effect below, not beside the render that consumes it: that
  // effect's resume path calls `adoptBranch`, and a dependency array is evaluated during
  // render, so a later declaration is a TDZ error rather than a style preference.
  //
  // `branchTargetIdRef` is read by `handleSave` / `handleSubmit` rather than the render
  // closure, for the same reason `metadataRef` is: both run inside a chain the advisor
  // started, and a captured value would be the one from before whatever that chain's earlier
  // links changed. It mirrors the COMPOSED branch, so a target that failed to load is never
  // named — that would make BMO record a swap the advisor never saw.
  const {
    effectiveTemplate,
    payloadTemplate,
    formBranch,
    onFormBranchChange,
    loading: branchLoading,
    loadFailed: branchLoadFailed,
    activeTargetIdRef: branchTargetIdRef,
    adoptBranch,
  } = useFormBranch({ template, loadTemplate: workflowsApi.get });

  // Pinned at mount so the id arriving mid-session does NOT remount FormShell — see the
  // class-level comment. `useState` with an initializer, not `useRef`, purely so the intent
  // ("this value is fixed for the life of the mount") is on the line.
  const [shellKey] = useState(() => routeApplicationId ?? 'new');

  // BMO's `x-bmo-idp-correlation-id` — one value per advisor form session, sent on every BMO call
  // this session makes so BMO can tie the whole sitting together (first save through submit, once
  // that contract lands). Same mount-pinned idiom as `shellKey` above, for three reasons:
  //
  //   - NOT derived from `applicationId`. That arrives late on `/new`, so keying off it would
  //     split one continuous sitting across two ids at the exact moment BMO starts caring.
  //   - React memory, not sessionStorage. Two tabs are two heaps, so each mints its own id —
  //     including a DUPLICATED tab, which would have inherited a cloned sessionStorage value and
  //     reported two concurrent sessions as one. This is the same isolation primitive the rest of
  //     this page relies on (see the header comment).
  //   - A reload therefore mints a new id, which is correct rather than a gap: a reload already
  //     discards every answer, so it is a new session in every sense that matters.
  const [idpCorrelationId] = useState(() => uuidv4());

  useEffect(() => {
    if (!templateId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const fresh = await workflowsApi.get(templateId);
        if (cancelled) return;
        setTemplate(fresh);

        // Nothing to resume on `/new`: BMO has never heard of this application. Skipping the
        // read is not just an optimization — `applicationsApi.get` has no id to ask about.
        if (!routeApplicationId) return;

        try {
          const resumed = await applicationsApi.get(
            routeApplicationId,
            templateId,
            idpCorrelationId,
          );
          if (cancelled) return;
          setMetadata(resumed.metadata);

          // SFA-8: an application is shown on the LATEST published version until it is
          // submitted, and on the version it was SUBMITTED on after that — the form must read
          // as what was actually filed. Past submission, reload the form on its own version when
          // today's differs. The server hands back only approved versions, and falls back to
          // the current one if that version cannot be found; the banner below says which.
          const savedVersion = resumed.metadata.templateVersion;
          const locked = isLocked(resumed.metadata.bmoStatus);
          // A form branch fired while this application was being filled. BMO records only
          // THAT one did — the answer naming which form belongs to the base form and is
          // never persisted — so the rule the base template declares is what identifies it.
          // This is the whole reason the convention is one branch rule per workflow.
          const resumedBranch = resumed.metadata.subsequentApplication
            ? (declaredBranches(fresh.steps)[0] ?? null)
            : null;
          // The one place `resumedOn` is written — see its declaration. Set here, before the
          // version-pinning fetches below, so the banner describes what BMO reported rather than
          // whether we managed to load it.
          if (savedVersion) {
            setResumedOn({
              formId: resumedBranch?.branch.targetTemplateId ?? templateId,
              version: savedVersion,
            });
          }

          if (resumedBranch) {
            // `savedVersion` is the BRANCH's label, not the base form's (the version has to
            // describe the content that was actually filled). So the SFA-8 re-pin applies to
            // the branch template, and pinning the base to it would ask for a version that
            // workflow never had. The base prefix stays on today's version, which is what the
            // versionNotice below is for.
            const targetId = resumedBranch.branch.targetTemplateId;
            try {
              const target = await workflowsApi.get(
                targetId,
                locked && savedVersion ? { versionLabel: savedVersion } : undefined,
              );
              if (cancelled) return;
              // `adoptBranch`, not the live-swap path: this target is version-pinned and the
              // hook's own loader would refetch it unpinned. Seeding the hook's cache with
              // what we already hold is what keeps the resumed form on the version it was
              // filed against.
              adoptBranch(target);
            } catch {
              // Same fallback as a live swap whose target cannot be loaded: stay on the base
              // form rather than strand the advisor on a half-composed workflow.
              if (cancelled) return;
            }
          } else if (locked && savedVersion && savedVersion !== fresh.servedVersionLabel) {
            const onSavedVersion = await workflowsApi.get(templateId, {
              versionLabel: savedVersion,
            });
            if (cancelled) return;
            setTemplate(onSavedVersion);
          }
          // The wire types each value as `unknown` because BMO stores whatever the field
          // produced. This is the boundary where it becomes a FieldValue again: we are
          // reading back exactly what buildApplicationSnapshot wrote, so the assertion is
          // over our own round trip, not over arbitrary foreign data.
          //
          // Advisor-sourced slots (`sourceValue: 'advisor.*'`, `advisorRepCodeSelector`) arrive
          // here too, but they are NOT the last word: they hold the advisor identity as of the
          // last save, and FormShell re-resolves it from the live /me session on mount
          // (advisorPrefill.ts → decideAdvisorPrefill, ADR-0010). A resumed draft must never
          // file under the details of who the advisor was — or of a colleague who saved it.
          if (resumed.fieldValuesStatus !== 'restored') {
            setResumeBlocked('unreadable');
            return;
          }
          const snapshotValues = restoreValuesFromSnapshot({
            plctData: resumed.fieldValues as Record<string, SnapshotFieldEntry>,
          });
          // An in-progress application follows the latest version, where a choice it answered
          // may no longer exist — drop those so the question reads as unanswered and the resume
          // opens on it. A submitted one is shown on its own version, exactly as filed.
          //
          // Pruning is skipped entirely for a branched resume: the answers belong to the
          // BRANCH form, and `fresh` is the base, so every one of them would read as a choice
          // on a field this template does not have. The branch's own template is not loaded
          // yet at this point, and a wrong prune would silently erase the application.
          const restored =
            isLocked(resumed.metadata.bmoStatus) || resumedBranch
              ? snapshotValues
              : pruneStaleChoices(fresh, snapshotValues).values;
          // Put the triggering answer back. It belongs to the base form, so it was never
          // persisted — and without it the radio renders unanswered, `resolveActiveBranch`
          // returns null on the very next render, and the branch unmounts itself the moment
          // the resume finishes. Seeding it from the RULE is exactly as sound as reading it
          // back would have been: the rule is what selected this form in the first place.
          if (resumedBranch) {
            restored[resumedBranch.branch.fieldId] = resumedBranch.branch.optionValue;
          }
          setInitialValues(restored);
          setIsResumed(Object.keys(restored).length > 0);
        } catch (resumeErr) {
          if (cancelled) return;
          // Any failure other than "no retrieve here" is a genuine load error and surfaces as
          // one. "No retrieve here" used to fall through to a blank, editable form — which is
          // exactly the overwrite the guard above exists to prevent, so it now blocks too.
          if (!isResumeUnavailable(resumeErr)) throw resumeErr;
          setResumeBlocked('unavailable');
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `idpCorrelationId`, `setMetadata` and `adoptBranch` are all mount-stable, so they are
    // here for exhaustive-deps only — none of them can re-run this effect.
  }, [templateId, routeApplicationId, idpCorrelationId, setMetadata, adoptBranch]);

  // One key per mounted application, reused across every save it makes. Two saves of two
  // different snapshots are different logical writes, so the key alone is not enough to
  // deduplicate them — FormShell serializes saves (it disables the nav actions while one is
  // in flight), which is what makes reuse safe here.
  //
  // Reuse earns its keep on the FIRST save especially: that call is what mints the application, so
  // a retry after a dropped response has to be recognizable to BMO as the same click rather than a
  // second application.
  const idempotencyKeyRef = useRef(uuidv4());

  const handleSave = useCallback(
    async (
      snapshot: ApplicationSnapshot,
      bmoStatus: string,
      eSign: ESignSubmission,
      // Minted by FormShell, once per press, and forwarded untouched — the deliver call that
      // follows this save composes the same §14.1 names from the same value.
      deliveryTimestamp?: string,
    ) => {
      if (!templateId) return;
      const saved = await applicationsApi.save({
        // Undefined on the first save of a `/new` application, which is what routes the call to
        // the create endpoint and asks BMO to mint an id.
        applicationId,
        templateId,
        // Names where the CONTENT came from when a form branch has fired. `templateId` above
        // stays the base form — it is what identifies the application at BMO — while this is
        // what the version label and the PDF / e-sign / amount resolvers are read from.
        subsequentTemplateId: branchTargetIdRef.current,
        // The snapshot's OWN language — the client's, picked on the Welcome step's radio (and
        // falling back to this advisor's locale when the workflow has no such radio). NOT the
        // page-level `language` above: that is the advisor's browser/header locale, so sending it
        // made `appInfo.appLanguage` report the language the ADVISOR was reading in while the
        // PDFs, signer locales and `plctData` labels all followed the client's choice. An
        // English-locale advisor picking French on the Welcome step saved `appLanguage: "en"`
        // against a French application.
        language: snapshot.language,
        // Absent before BMO has stamped one, i.e. on that first save; BMO stamps it there.
        creationTimestamp: metadata?.creationTimestamp ?? '',
        // Present only on a save that a delivery follows. It stamps the advisor's uploads'
        // §14.1 names, and the deliver call recomposes them from this exact value.
        deliveryTimestamp,
        fieldValues: snapshot.plctData,
        // Where the advisor has reached on the workflow, computed by FormShell's ladder
        // (form-renderer/bmoStatus.ts) and travelling with the values it describes. The save
        // endpoint rejects anything BMO owns, so a stale view can't overwrite their status.
        bmoStatus,
        // ...and where it is moving FROM: BMO's own last word on this application, NOT the
        // shell's local ladder value. The BFF refuses the save when this one is BMO-owned, which
        // is what stops a stale tab walking an e-signing application back to IN-PROGRESS.
        // Undefined until BMO has confirmed something, which skips the check server-side.
        knownBmoStatus: metadata?.bmoStatus,
        // Signers + uploaded supporting documents. Always sent, with empty arrays until the
        // workflow's e-signature step has a roster to resolve — empty is what keeps `bmoData` off
        // the wire entirely, since BMO's schema cannot accept a partial one. The create call
        // ignores it whatever it carries: its file names are built around the application id BMO
        // has not minted yet, and the next save re-sends the roster in full.
        eSign,
        idempotencyKey: idempotencyKeyRef.current,
        // Constant across every save this mount makes — including the ones after the URL rewrite
        // below, which is the whole point: BMO sees one session, not one before the id and
        // another after it.
        idpCorrelationId,
      });
      setMetadata(saved);

      // First save of a `/new` application: put BMO's id in the URL so the tab is bookmarkable,
      // refreshable and self-identifying from here on, exactly as it was when the id was minted on
      // load.
      //
      // `history.replaceState` rather than react-router's `navigate`: a router navigation re-runs
      // the route match and remounts this subtree, which would throw away every answer the advisor
      // just typed — the very work this save was protecting. Bypassing the router leaves
      // `useParams()` reporting the old path, which is why `applicationId` above reads from
      // `metadata` first. `replace`, not `push`, so Back still leaves the application rather than
      // landing on a `/new` URL that would start a second one.
      if (!applicationId) {
        window.history.replaceState(
          null,
          '',
          `/advisor/workflows/${templateId}/applications/${encodeURIComponent(saved.applicationId)}${location.search}`,
        );
      }
    },
    [
      templateId,
      applicationId,
      location.search,
      metadata?.creationTimestamp,
      metadata?.bmoStatus,
      // All mount-stable — listed for exhaustive-deps only, none invalidates this callback.
      // `branchTargetIdRef` is a ref precisely so its CURRENT value is read at call time
      // rather than captured here; it appears in this list only because the lint rule cannot
      // tell a ref apart from a value once it arrives through a hook's return.
      idpCorrelationId,
      setMetadata,
      branchTargetIdRef,
    ],
  );

  /**
   * The advisor's final submit. Deliberately NOT a wrapper around `handleSave` — see
   * `FormShell`'s `onSubmit` doc comment for why this is a dedicated BMO call rather than a save
   * carrying a different status.
   *
   * Unreachable before the first save has minted an `applicationId`: Submit sits on the advisor's
   * LAST step, well past the first saving Next, and the chain it heads re-saves before it submits
   * — so by the time this runs the id is always populated.
   *
   * Every piece of BMO's own state is read from {@link metadataRef}, NOT from the render closure:
   * the save immediately before this one is part of the same press, so a captured `metadata` is
   * one call out of date. See the ref's own comment for what that broke.
   */
  const handleSubmit = useCallback(
    async (
      snapshot: ApplicationSnapshot,
      eSign: ESignSubmission,
      additionalDocumentKeys: string[],
      pdfValues: Record<string, unknown>,
    ) => {
      const known = metadataRef.current;
      // Same precedence as `applicationId` below: BMO's reply to the chain's own save outranks
      // the route, and on `/new` it is the only place the id exists at all.
      const submitApplicationId = known?.applicationId ?? applicationId;
      if (!templateId || !submitApplicationId) {
        throw new Error('Cannot submit before the application has been saved.');
      }
      const result = await applicationsApi.submit({
        applicationId: submitApplicationId,
        templateId,
        // See `handleSave`. Submit needs it for one more reason: the delivered package is
        // built from this template's approved version, so omitting it would ship the base
        // form's PDFs for an application filled on another form entirely.
        subsequentTemplateId: branchTargetIdRef.current,
        language: snapshot.language,
        creationTimestamp: known?.creationTimestamp ?? '',
        fieldValues: snapshot.plctData,
        // The package's PDFs fill from these, not from `fieldValues` — see
        // `applicationsApi.submit`. Resolved by FormShell through the same funnel its
        // previews use, so what BMO receives is the document the advisor saw.
        pdfValues,
        knownBmoStatus: known?.bmoStatus,
        eSign,
        additionalDocumentKeys,
        idpCorrelationId,
      });
      // BMO's own word on the application's status from here on — mirrors how `handleSave`
      // folds BMO's response back into metadata, so a page reload (which loses FormShell's own
      // ladder state) still reads the application as submitted rather than IN-PROGRESS.
      setMetadata((prev) => (prev ? { ...prev, bmoStatus: result.bmoStatus } : prev));
      return { deliveryStatus: result.deliveryStatus };
    },
    // No `metadata` members here any more — they are read through the ref, which is the point.
    // `setMetadata` is mount-stable (an empty-dep `useCallback`), so it never invalidates this.
    // `branchTargetIdRef` is a ref and is read at call time for the same reason `metadataRef`
    // is; it is listed only because the lint rule cannot recognise one once it arrives
    // through a hook's return value.
    [templateId, applicationId, idpCorrelationId, setMetadata, branchTargetIdRef],
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }
  if (loadError || !template) {
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Alert type="error" message={loadError ?? t('advisor.application.notFound')} />
        <div>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/advisor/dashboard')}>
            {t('advisor.application.backToDashboard')}
          </Button>
        </div>
      </div>
    );
  }

  /**
   * Tells the advisor when the form in front of them is not the version the application was
   * saved on, or not today's (Lintao's review of SFA-8). Silent otherwise — a brand-new
   * application, or one saved on the version still current, has nothing to explain.
   *
   * It speaks for the RESUME alone ({@link resumedOn}), never for the live session: an advisor
   * is only ever shown the form they are filling, so nothing can move under them between the
   * page opening and their submit.
   */
  const versionNotice = (() => {
    if (!resumedOn) return null;
    // Which workflow is on screen. `effectiveTemplate` carries the BRANCH's version labels once
    // one is composed in — identity comes from the base form, versions from the target — so the
    // composed template's own `id` is the base's and cannot answer this.
    const shownFormId = formBranch?.targetTemplateId ?? templateId;
    // A different form than the one resumed: the advisor has swapped branches this session (or
    // the resumed branch has not composed in yet). Two forms' labels say nothing about each
    // other, and a swap is not an update, so there is nothing to announce either way.
    if (shownFormId !== resumedOn.formId) return null;
    const shownTemplate = effectiveTemplate ?? template;
    const saved = resumedOn.version;
    const shown = shownTemplate.servedVersionLabel;
    const current = shownTemplate.currentVersionLabel;
    if (!shown) return null;
    const submitted = isLocked(metadata?.bmoStatus);
    if (submitted) {
      if (saved !== shown) {
        // Its own version could not be loaded, so it is on today's. Say so rather than let it
        // pass as the form that was filed.
        return { type: 'warning' as const, key: 'submittedFallback', params: { saved, shown } };
      }
      return current && current !== shown
        ? { type: 'info' as const, key: 'submittedOn', params: { saved, current } }
        : null;
    }
    // Still being filled: it follows the latest version, so the form may have changed under
    // answers given on the earlier one.
    return saved !== shown
      ? { type: 'warning' as const, key: 'updated', params: { saved, shown } }
      : null;
  })();

  if (resumeBlocked) {
    // No FormShell at all, rather than a read-only one: with nothing restored there is nothing to
    // read, and not mounting the shell is what guarantees no save path exists to reach.
    return (
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Alert
          type="warning"
          showIcon
          message={t('advisor.application.resumeBlocked.title')}
          description={t(`advisor.application.resumeBlocked.${resumeBlocked}`)}
        />
        <div>
          <Button onClick={() => window.location.reload()}>
            {t('advisor.application.resumeBlocked.retry')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header strip identifying the application: the workflow name, and BMO's application id
          as an "APP ID #…" pill so the advisor (and QA) can tell their open tabs apart. */}
      <div
        style={{
          padding: '16px 0 8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Text strong style={{ fontSize: 18, color: '#0f172a' }}>
          {(isFr ? template.nameFr : template.nameEn) ||
            template.nameEn ||
            template.appName ||
            t('advisor.application.untitled')}
        </Text>
        {/* "CLIENT: …" then "APP ID #…" pills on the right, per the Smart Form design. The id pill
            appears once the first save has minted BMO's application id. */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <ClientBadge name={clientName} />
          <AppIdBadge id={applicationId} />
        </div>
      </div>
      {/* key={shellKey} — see the class-level comment. Two applications in the
          same tab force a full FormShell remount so state resets. Pinned to the
          id this page MOUNTED with, so the id arriving on the first save does
          not remount the shell out from under the advisor's answers. */}
      {versionNotice && (
        <Alert
          type={versionNotice.type}
          showIcon
          style={{ marginBottom: 12 }}
          message={t(
            `advisor.application.versionNotice.${versionNotice.key}`,
            versionNotice.params,
          )}
        />
      )}
      {/* Fetching the form the advisor just selected. Rendered ABOVE the shell rather than
          in place of it: swapping in the page-level <Spin /> would unmount FormShell and
          take every answer with it. */}
      {branchLoading && (
        <Alert
          type="info"
          style={{ marginBottom: 12 }}
          icon={<Spin size="small" />}
          showIcon
          message={t('advisor.application.loadingBranchForm')}
        />
      )}
      {/* The selected form could not be loaded — unpublished, archived, gated out or gone.
          A warning rather than a page error: the base form is still perfectly fillable, and
          every answer already given is intact. Said out loud rather than swallowed, because
          a silent fallback here is indistinguishable from the rule never having fired. */}
      {branchLoadFailed && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('advisor.application.branchFormUnavailable')}
        />
      )}
      <FormShell
        key={shellKey}
        // The COMPOSED template once a branch is active — never a different `key`. A remount
        // here would discard every answer the advisor has given, including the one that
        // selected this form, which would immediately un-select it again.
        template={effectiveTemplate ?? template}
        payloadTemplate={payloadTemplate}
        formBranch={formBranch}
        // The answers now select a different form for the rest of the workflow. Loading and
        // composing it is `useFormBranch`'s job; the shell only reports the selection.
        onFormBranchChange={onFormBranchChange}
        language={language}
        mode="advisor"
        // Names every additional document this advisor uploads, per spec §14.1. This is BMO's own
        // application id — undefined until the first save mints it, which leaves the upload path
        // unavailable until then (useAdditionalDocumentUpload). Unreachable in practice: the
        // AdditionalDocuments step sits well past the first saving Next.
        applicationId={applicationId}
        initialValues={initialValues}
        isResumedApplication={isResumed}
        onPrimaryClientNameChange={setClientName}
        // Seeds the shell's status ladder, and read-onlys the whole form when BMO has taken the
        // application over (SUBMITTED, any ESIGN-*, CLOSED…). Undefined until a resume or a save
        // has landed, which the shell reads as "brand new, start at IN-PROGRESS" — deliberately
        // NOT as locked. No `readOnly` prop here: the lock derives from the status inside the
        // shell so there is one source of truth for it.
        bmoStatus={metadata?.bmoStatus}
        // Read by the dev-only eSignInfo probe alone — it is the instant §14.1 package delivery
        // names are stamped with, so passing it keeps the names that probe shows identical to the
        // ones the next save sends. BMO owns the value; we only carry it.
        creationTimestamp={metadata?.creationTimestamp}
        onSave={handleSave}
        onSubmit={handleSubmit}
        // No `onSaveAndExit`: the button is parked for now, so the shell must not render it.
        // Absence is the gate (the same idiom that withholds it from a public customer) —
        // pass the handler back (`() => navigate('/advisor/dashboard')`) to restore it.
        // Nothing else changes: Next and Submit still push the full snapshot to BMO.
        // INFLOWS-486 #1: Cancel on the Welcome step leaves for BMO's Smart Forms library —
        // the list the advisor picked this form from. Keyed on `language`, the ADVISOR's own
        // locale, not `snapshot.language` (the client's chosen document language): this is the
        // advisor-facing page they came from, the same locale the Welcome card's copy reads in.
        // Without a configured BMO page for that locale it keeps the in-app exit.
        onWelcomeCancel={() => {
          const landing = smartFormLandingUrl(getRuntimeConfig(), language);
          if (landing) window.location.assign(landing);
          else navigate('/advisor/dashboard');
        }}
        // An answer that belongs on another form starts a FRESH application
        // there — `/new` mints nothing until its own first save, so an advisor
        // who redirects and then backs out leaves no half-built record behind.
        // The application they are leaving keeps whatever BMO already holds:
        // this deliberately does NOT save on the way out, because the answer
        // driving the redirect is one this form cannot carry.
        onOptionRedirect={(target) =>
          followOptionRedirect(
            target,
            navigate,
            (id) => `/advisor/workflows/${id}/applications/new`,
          )
        }
      />
    </div>
  );
}
